import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOne, useList } from "@refinedev/core";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";
import { formatDate } from "@/lib/formatters";

export function CerrarCortePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: periodo } = useOne({ resource: "periodos_cobro", id });
  const { data: ventas } = useList({
    resource: "ventas",
    filters: id
      ? [
          { field: "periodo_cobro_id", operator: "eq", value: id },
          { field: "estado", operator: "eq", value: "confirmada" },
        ]
      : [],
    pagination: { mode: "off" },
  });
  const { data: pagos } = useList({
    resource: "venta_pagos",
    filters: ventas?.data
      ? [
          {
            field: "venta_id",
            operator: "in",
            value: ventas.data.map((v) => v.id as string),
          },
        ]
      : [],
    meta: { select: "*, financieras(nombre)" },
    pagination: { mode: "off" },
  });

  if (!periodo?.data) return <div className="card p-8 text-center">Cargando...</div>;
  const p = periodo.data;

  // Agrupar por financiera/efectivo
  const grupos: Record<string, { cantidad: number; total: number }> = {};
  pagos?.data.forEach((pg) => {
    const key = pg.tipo === "efectivo" ? "Efectivo" : (pg.financieras as { nombre: string })?.nombre ?? "?";
    if (!grupos[key]) grupos[key] = { cantidad: 0, total: 0 };
    grupos[key].cantidad += 1;
    grupos[key].total += pg.monto as number;
  });

  const totalVentas = ventas?.data.reduce((s, v) => s + (v.valor_total as number), 0) ?? 0;

  async function cerrar() {
    setSubmitting(true);
    const { error } = await supabase.rpc("fn_cerrar_corte", { p_periodo_id: id });
    setSubmitting(false);
    if (error) {
      toast.error("Error al cerrar", { description: error.message });
      return;
    }
    toast.success("Corte cerrado. Esperando liquidación del central.");
    navigate("/cuentas-cobro");
  }

  return (
    <div className="max-w-xl space-y-4">
      <PageHeader title={`Cerrar corte #${p.numero_correlativo}`} />

      <div className="card p-4 space-y-1">
        <p className="text-sm text-fg-muted">
          Del {formatDate(p.fecha_inicio)} al {formatDate(p.fecha_fin)}
        </p>
        <p className="text-2xl font-bold">{ventas?.data.length} ventas</p>
        <MoneyDisplay value={totalVentas} className="text-lg" />
      </div>

      <section className="card p-4 space-y-2">
        <h2 className="font-semibold">Por forma de pago</h2>
        <ul className="space-y-1 text-sm">
          {Object.entries(grupos).map(([nombre, { cantidad, total }]) => (
            <li key={nombre} className="flex justify-between">
              <span>
                {nombre} ({cantidad})
              </span>
              <MoneyDisplay value={total} />
            </li>
          ))}
        </ul>
      </section>

      <div className="card p-3 bg-warning/10 border-warning text-sm">
        ⚠ Una vez cierres, no podrás editar las ventas de este corte.
      </div>

      <div className="flex gap-2">
        <button onClick={() => setConfirm(true)} className="btn-primary flex-1">
          Cerrar y mandar a liquidar
        </button>
        <button onClick={() => navigate(-1)} className="btn-secondary">Cancelar</button>
      </div>

      <ConfirmDialog
        open={confirm}
        title="¿Cerrar este corte?"
        description="Esta acción manda el corte al central para que lo liquide. No podrás editar las ventas después."
        variant="destructive"
        confirmLabel={submitting ? "Cerrando..." : "Sí, cerrar corte"}
        onConfirm={cerrar}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
}
